import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthProfile } from 'src/app/shared/models/auth-profile.interface';
import { AuthProfileService } from '../services/auth-profile.service';
import { firstValueFrom } from 'rxjs';
import { signal } from '@angular/core';
import { ToastService } from 'src/app/toast/toast.service';
import { HttpClient } from '@angular/common/http';
import { ConfirmationService } from 'primeng/api';
import { AuthService } from '../../../auth/login/services/auth.service';

export interface ClientTenant {
  id?: string;
  clientId?: string;
  tenantId: string;
  tenantName: string;
  tenantUrl: string;
}

export interface Client {
  id: number;
  name: string;
  description?: string;
  clientTenants?: ClientTenant[];
}

export class AuthProfileViewModel {
  authProfiles: AuthProfile[] = [];
  private allAuthProfiles: AuthProfile[] = []; // Cache for filtering
  selectedProfile: AuthProfile | null = null;
  isEditMode: boolean = false;
  profileForm!: FormGroup;

  clientList = signal<{ name: string; code: string }[]>([]);
  tenantList = signal<{ name: string; code: string }[]>([]);
  tenantTypes = signal<any[]>([]);
  selectedClientId = signal<string | null>(null);
  selectedTenantTypeId = signal<string | null>(null);
  clients: any[] = [];
  showPassword = signal<boolean>(false);

  // Use WritableSignal for two-way binding support
  private _displayDialog = signal<boolean>(false);
  private originalFormValue: any = null;
  private formValueChanged = signal<number>(0);

  // Getter and setter for template binding
  get displayDialog(): boolean {
    return this._displayDialog();
  }

  set displayDialog(value: boolean) {
    this._displayDialog.set(value);
  }

  profileGroups: any[] = [];
  filteredProfileGroups: string[] = [];

  // Signal to control client filter dropdown disabled state
  isClientFilterDisabled = signal<boolean>(false);

  constructor(
    private fb: FormBuilder,
    private authProfileService: AuthProfileService,
    private toastService: ToastService,
    private http: HttpClient,
    private confirmationService: ConfirmationService,
    private authService: AuthService
  ) {
    this.initializeForm();
  }

  isSystemUser(): boolean {
    const userInfo = this.authService.getUserInfoFromToken();
    return userInfo?.isSystemUser === true;
  }

  // Computed signal for client filter dropdown options
  clientFilterOptions = (): { label: string; value: string | null }[] => {
    const clients = this.clientList();
    const clientOptions = clients.map(client => ({
      label: client.name,
      value: client.code
    }));

    // Only add "Default" option for system users
    if (this.isSystemUser()) {
      return [
        { label: 'Master Client', value: null },
        ...clientOptions
      ];
    }

    return clientOptions;
  };

  // Computed signal for tenant type filter dropdown options
  tenantTypeFilterOptions = (): { label: string; value: string | null }[] => {
    const tenants = this.tenantList();
    return [
      ...tenants.map(tenant => ({
        label: tenant.name,
        value: tenant.code
      }))
    ];
  };

  // Computed signal for modal client dropdown options (with Default)
  clientModalOptions = (): { label: string; value: string | null }[] => {
    const clients = this.clientList();
    const clientOptions = clients.map(client => ({
      label: client.name,
      value: client.code
    }));

    // Only add "Default" option for system users
    if (this.isSystemUser()) {
      return [
        { label: 'Master Client', value: null },
        ...clientOptions
      ];
    }

    return clientOptions;
  };

  // Handler for client filter change
  onClientChange(event: any): void {
    const selectedCode = event.value;
    this.selectedClientId.set(selectedCode);
    this.applyFilters();
  }

  // Handler for tenant type filter change
  onTenantTypeChange(event: any): void {
    const selectedCode = event.value;
    this.selectedTenantTypeId.set(selectedCode);
    this.applyFilters();
  }

  // Apply nested filters to auth profiles
  private applyFilters(): void {
    let profiles = [...this.allAuthProfiles]; // Start with cached full list

    // Apply client filter
    const clientId = this.selectedClientId();
    if (clientId !== null) {
      // Filter by specific client ID
      profiles = profiles.filter((profile: AuthProfile) => profile.clientId === clientId);
    } else {
      // Filter by profiles with null clientId (Default)
      profiles = profiles.filter((profile: AuthProfile) => profile.clientId === null);
    }
    // If clientId is undefined, show all profiles (no filter applied)

    // Apply tenant type filter
    const tenantTypeId = this.selectedTenantTypeId();
    if (tenantTypeId) {
      profiles = profiles.filter((profile: AuthProfile) => profile.tenantType === tenantTypeId);
    }

    this.authProfiles = profiles;

    // Extract unique profile groups from filtered profiles
    const uniqueGroups = [...new Set(
      profiles
        .map((profile: AuthProfile) => profile.profileGroup)
        .filter((group): group is string => !!group) // Type guard to ensure string
    )];

    this.profileGroups = uniqueGroups.map(group => ({ value: group, label: group }));
  }

  // Computed signal to check if modal client dropdown should be disabled
  isModalClientDropdownDisabled = (): boolean => {
    const clients = this.clientList();
    // Disable if non-system user (they only have one client)
    return !this.isSystemUser() && clients.length === 1;
  };

  private initializeForm(): void {
    this.profileForm = this.fb.group({
      clientId: [null],
      tenantType: [null, Validators.required],
      profileGroup: [null, [
        Validators.required,
        Validators.minLength(3)
      ]],
      tenantUrl: [null, [
        Validators.required,
        Validators.pattern(/^(http|https):\/\/[^\s$.?#].[^\s]*$/)
      ]],
      profileName: [null, [
        Validators.required,
        Validators.minLength(3)
      ]],
      username: [null, [
        Validators.required,
        Validators.minLength(3)
      ]],
      secretKey: [null, [
        Validators.required,
        // Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/)
      ]]
    });

    // Subscribe to form changes to trigger reactivity
    this.profileForm.valueChanges.subscribe(() => {
      this.formValueChanged.update(v => v + 1);
    });
  }

  async init(): Promise<void> {
    await this.loadAuthProfiles();
    this.loadClientList();
    this.loadTenantList();
    
    // Set initial filter to "Default" (null) for system users
    if (this.isSystemUser()) {
      this.selectedClientId.set(null);
      this.applyFilters();
    }
  }

  private loadClientList(): void {
    this.authProfileService.getMyClients().subscribe({
      next: (response: any) => {
        const list = response?.data || [];
        const mapped = list.map((client: any) => ({
          name: client.name,
          code: client.id
        }));
        this.clientList.set(mapped);
        
        // Auto-select and disable filter for non-system users with one client
        if (!this.isSystemUser() && mapped.length === 1) {
          this.selectedClientId.set(mapped[0].code);
          this.isClientFilterDisabled.set(true);
          this.applyFilters();
        } else {
          this.isClientFilterDisabled.set(false);
        }
      },
      error: () => {
        this.clientList.set([]);
      }
    });
  }

  private loadTenantList(): void {
    this.authProfileService.getTenantsAll().subscribe({
      next: (response: any) => {
        const tenants = Array.isArray(response) ? response : (response?.data || []);
        const mapped = tenants.map((tenant: any) => ({
          name: tenant.tenentType, // API has 'tenentType' (typo in backend)
          code: tenant.id
        }));
        this.tenantList.set(mapped);
        this.tenantTypes.set(tenants);
      },
      error: () => {
        this.tenantList.set([]);
        this.tenantTypes.set([]);
      }
    });
  }

  private async loadAuthProfiles(): Promise<void> {
    this.authProfileService.getAllAuthProfiles().subscribe({
      next: (response: any) => {
        const profiles = Array.isArray(response) ? response : (response?.data || []);

        this.allAuthProfiles = profiles;
        const uniqueGroups = [...new Set(
          profiles
            .map((profile: AuthProfile) => profile.profileGroup)
            .filter((group:any): group is string => !!group)
        )];

        this.profileGroups = uniqueGroups.map(group => ({ value: group, label: group }));
        
        if (this.selectedClientId() !== undefined) {
          this.applyFilters();
        } else {
          this.authProfiles = profiles;
        }
      },
      error: () => {
        this.authProfiles = [];
        this.allAuthProfiles = [];
        this.profileGroups = [];
      }
    });
  }

  prepareCreateMode(): void {
    this.isEditMode = false;
    this.selectedProfile = null;
    this.profileForm.reset();
    this.showPassword.set(false);
    this._displayDialog.set(true);
    
    // Auto-select and disable for non-system users with one client
    const clients = this.clientList();
    if (!this.isSystemUser() && clients.length === 1) {
      this.profileForm.patchValue({
        clientId: clients[0].code
      });
      this.profileForm.get('clientId')?.disable();
    } else {
      this.profileForm.get('clientId')?.enable();
    }
    
    this.profileForm.get('secretKey')?.setValidators([
      Validators.required,
    //  Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/)
    ]);
    this.profileForm.get('secretKey')?.updateValueAndValidity();
  }

  prepareEditMode(profile: AuthProfile): void {
    this.isEditMode = true;
    this.selectedProfile = profile;
    this.showPassword.set(false);
    this._displayDialog.set(true);

    const formValue = {
      clientId: profile.clientId,
      tenantType: profile.tenantType,
      profileGroup: profile.profileGroup,
      profileName: profile.profileName,
      tenantUrl: profile.tenantUrl,
      username: profile.username,
      secretKey: profile.secretKey
    };

    this.profileForm.patchValue(formValue, { emitEvent: false });

    // Always disable client dropdown in edit mode
    const clients = this.clientList();
    if (!this.isSystemUser() && clients.length === 1 && !profile.clientId) {
      this.profileForm.patchValue({
        clientId: clients[0].code
      });
    }
    this.profileForm.get('clientId')?.disable();

    // Store original values for comparison AFTER patching
    this.originalFormValue = this.profileForm.getRawValue();

    // Clear validators initially, but add them back if user starts typing
    const secretKeyControl = this.profileForm.get('secretKey');
    secretKeyControl?.clearValidators();
    secretKeyControl?.updateValueAndValidity();

    // Add validator dynamically when user modifies the password field
    secretKeyControl?.valueChanges.subscribe(value => {
      if (value && value !== this.originalFormValue.secretKey) {
        secretKeyControl.setValidators([
          Validators.required,
         // Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/)
        ]);
        secretKeyControl.updateValueAndValidity({ emitEvent: false });
      }
    });

    // Reset the change counter after setting up edit mode
    this.formValueChanged.set(0);
  }

  // Check if form has changes in edit mode
  hasFormChanges(): boolean {
    // Trigger reactivity by reading the signal
    this.formValueChanged();

    if (!this.isEditMode || !this.originalFormValue) {
      return true; // In create mode, always allow submit
    }

    const currentValue = this.profileForm.getRawValue();

    // Compare each field individually for better accuracy
    const hasChanges = Object.keys(this.originalFormValue).some(key => {
      const original = this.originalFormValue[key];
      const current = currentValue[key];
      return original !== current;
    });

    return hasChanges;
  }

  // Check if submit button should be disabled
  isSubmitDisabled(): boolean {
    // Trigger reactivity
    this.formValueChanged();

    return this.profileForm.invalid || (this.isEditMode && !this.hasFormChanges());
  }

  validateForm(): boolean {
    return this.profileForm.valid;
  }

  saveProfile(): void {
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      this.toastService.warn('Please fill all required fields');
      return;
    }

    // Use getRawValue() to include disabled fields
    const formValue = this.profileForm.getRawValue();

    const payload = this.isEditMode && this.selectedProfile?.id
      ? { ...formValue, id: this.selectedProfile.id }
      : formValue;

    const action = this.isEditMode ? 'updated' : 'created';

    this.authProfileService.save(payload).subscribe({
      next: () => {
        this.toastService.success(`Auth profile ${action} successfully`);
        this.loadAuthProfiles();
        // Reapply filters after reload
        setTimeout(() => this.applyFilters(), 100);
        this._displayDialog.set(false);
      },
      error: (error) => {
        this.toastService.error(`Failed to ${this.isEditMode ? 'update' : 'create'} auth profile. Tenant Type, Profile Name and Group are already exist`);
        console.error(`${action} error:`, error);
      }
    });
  }

  isProfileIncomplete(profile: AuthProfile): boolean {
    return !profile.username || !profile.secretKey;
  }

  deleteProfile(profile: any): void {
    if (!profile?.id) {
      this.toastService.warn('Invalid profile');
      return;
    }

    this.confirmationService.confirm({
      message: `Are you sure you want to delete auth profile "${profile.profileName}"?`,
      header: 'Confirm Delete',
      acceptButtonStyleClass: 'p-button-success',
      rejectButtonStyleClass: 'p-button-warn',
      accept: () => {
        this.authProfileService.removeAuthProfile(profile.id).subscribe({
          next: () => {
            this.toastService.success('Auth profile deleted successfully');
            this.loadAuthProfiles();
            // Reapply filters after reload
            setTimeout(() => this.applyFilters(), 100);
          },
          error: (error) => {
            this.toastService.error('Failed to delete auth profile');
            console.error('Delete error:', error);
          }
        });
      }
    });
  }

  resetForm(): void {
    this.profileForm.reset();
    this.selectedProfile = null;
    this.isEditMode = false;
  }

  filterProfileGroups(event: any) {
    const query = (event.query || '').trim();
    
    // Get selected client from the modal form (not the filter)
    const modalClientId = this.profileForm.get('clientId')?.value;
    
    // Filter profiles by the modal's selected client to get relevant groups
    let relevantProfiles = [...this.allAuthProfiles];
    if (modalClientId !== null && modalClientId !== undefined) {
      relevantProfiles = relevantProfiles.filter(profile => profile.clientId === modalClientId);
    } else {
      // If "Default" (null) is selected, show groups for profiles with null clientId
      relevantProfiles = relevantProfiles.filter(profile => profile.clientId === null);
    }
    
    // Extract unique groups from relevant profiles
    const existingGroups = [...new Set(
      relevantProfiles
        .map(profile => profile.profileGroup)
        .filter((group): group is string => !!group)
    )];

    if (!query) {
      this.filteredProfileGroups = [...existingGroups];
      return;
    }

    this.filteredProfileGroups = existingGroups
      .filter(group => group.toLowerCase().includes(query.toLowerCase()));
    const exactMatch = existingGroups.some(g => g.toLowerCase() === query.toLowerCase());
    if (query && !exactMatch) {
      this.filteredProfileGroups.unshift(query);
    }
  }

  isExistingGroup(group: string): boolean {
    if (!group) return false;
    
    // Get selected client from the modal form
    const modalClientId = this.profileForm.get('clientId')?.value;
    
    // Filter profiles by the modal's selected client
    let relevantProfiles = [...this.allAuthProfiles];
    if (modalClientId !== null && modalClientId !== undefined) {
      relevantProfiles = relevantProfiles.filter(profile => profile.clientId === modalClientId);
    } else {
      relevantProfiles = relevantProfiles.filter(profile => profile.clientId === null);
    }
    
    const relevantGroups = [...new Set(
      relevantProfiles
        .map(profile => profile.profileGroup)
        .filter((g): g is string => !!g)
    )];
    
    return relevantGroups.some(g => g.toLowerCase() === group.toLowerCase());
  }

  onProfileGroupClear(): void {
    this.filteredProfileGroups = [];
  }

  togglePasswordVisibility(): void {
    this.showPassword.update(visible => !visible);
  }

  closeDialog(): void {
    this.resetForm();
    this.originalFormValue = null;
    this.formValueChanged.set(0);
    this._displayDialog.set(false);
  }

  shouldShowPasswordError(): boolean {
    const secretKeyControl = this.profileForm.get('secretKey');
    if (!secretKeyControl) return false;

    // In create mode, always show errors if touched and invalid
    if (!this.isEditMode) {
      return secretKeyControl.invalid && secretKeyControl.touched;
    }

    // In edit mode, only show errors if user has modified the field
    const currentValue = secretKeyControl.value;
    const hasBeenModified = currentValue !== this.originalFormValue?.secretKey;
    return hasBeenModified && secretKeyControl.invalid && secretKeyControl.touched;
  }


}
